/**
 * Voice AI — Self Healing Proposals (Tickets)
 * Cards for major change proposals with Execute/Deny/Edit workflow.
 */
import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { settingsService } from '../../services/settings-service.js';
import { liveCallsService } from '../../services/live-calls-service.js';
import { tz } from '../../services/timezone-service.js';

export class SelfHealingProposals extends LitElement {
  static properties = {
    _proposals: { state: true },
    _loading: { state: true },
    _page: { state: true },
    _totalPages: { state: true },
    _expandedId: { state: true },
    _editingId: { state: true },
    _editText: { state: true },
    _actionInProgress: { state: true },
    _filter: { state: true },
    _executingId: { state: true },
    _executionOutputs: { state: true },
    _expandedOutputId: { state: true },
  };

  static styles = [
    designSystem,
    componentStyles,
    css`
      :host { display: block; }

      .filter-row {
        display: flex;
        gap: var(--space-2);
        margin-bottom: var(--space-4);
      }
      .filter-btn {
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        color: var(--color-text-secondary);
        padding: var(--space-1) var(--space-3);
        border-radius: var(--radius-full);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      .filter-btn:hover { border-color: var(--color-border-hover); }
      .filter-btn.active {
        background: var(--color-primary);
        color: white;
        border-color: var(--color-primary);
      }

      .proposals-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
      }

      .proposal-card {
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-lg);
        overflow: hidden;
      }

      .proposal-header {
        padding: var(--space-4);
      }

      .proposal-status-row {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        margin-bottom: var(--space-2);
      }

      .status-badge {
        font-family: var(--font-mono);
        font-size: 0.625rem;
        padding: 2px 8px;
        border-radius: var(--radius-sm);
        text-transform: uppercase;
        font-weight: var(--font-bold);
      }
      .status-badge.pending { background: rgba(210,153,34,0.15); color: #d29922; }
      .status-badge.approved { background: rgba(63,185,80,0.15); color: #3fb950; }
      .status-badge.denied { background: rgba(248,81,73,0.15); color: #f85149; }
      .status-badge.executed { background: rgba(88,166,255,0.15); color: #58a6ff; }

      .severity-tag {
        font-family: var(--font-mono);
        font-size: 0.625rem;
        padding: 2px 6px;
        border-radius: var(--radius-sm);
      }
      .severity-tag.critical { background: rgba(248,81,73,0.15); color: #f85149; }
      .severity-tag.high { background: rgba(210,153,34,0.15); color: #d29922; }
      .severity-tag.medium { background: rgba(88,166,255,0.15); color: #58a6ff; }

      .category-tag {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }

      .proposal-title {
        font-family: var(--font-display);
        font-size: var(--text-base);
        font-weight: var(--font-semibold);
        color: var(--color-text-primary);
        margin: 0 0 var(--space-2) 0;
      }

      .proposal-date {
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }

      .proposal-plan-toggle {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2) var(--space-4);
        background: var(--color-bg-primary);
        border-top: 1px solid var(--color-border-secondary);
        cursor: pointer;
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-primary);
        user-select: none;
      }
      .proposal-plan-toggle:hover {
        background: var(--color-bg-tertiary);
      }

      .proposal-plan {
        padding: var(--space-4);
        background: #0d1117;
        border-top: 1px solid #30363d;
        font-family: var(--font-mono);
        font-size: 0.75rem;
        line-height: 1.7;
        color: #c9d1d9;
        white-space: pre-wrap;
        word-break: break-word;
        max-height: 400px;
        overflow-y: auto;
      }

      .proposal-actions {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-3) var(--space-4);
        border-top: 1px solid var(--color-border-secondary);
      }

      .action-btn {
        padding: var(--space-2) var(--space-3);
        border-radius: var(--radius-sm);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        font-weight: var(--font-medium);
        cursor: pointer;
        border: 1px solid transparent;
        transition: all var(--transition-fast);
        display: flex;
        align-items: center;
        gap: var(--space-1);
      }
      .action-btn:disabled {
        opacity: 0.5;
        cursor: default;
      }

      .btn-execute {
        background: rgba(63,185,80,0.15);
        color: #3fb950;
        border-color: rgba(63,185,80,0.3);
      }
      .btn-execute:hover:not(:disabled) { background: rgba(63,185,80,0.25); }

      .btn-deny {
        background: rgba(248,81,73,0.15);
        color: #f85149;
        border-color: rgba(248,81,73,0.3);
      }
      .btn-deny:hover:not(:disabled) { background: rgba(248,81,73,0.25); }

      .btn-edit {
        background: rgba(88,166,255,0.15);
        color: #58a6ff;
        border-color: rgba(88,166,255,0.3);
      }
      .btn-edit:hover:not(:disabled) { background: rgba(88,166,255,0.25); }

      .edit-area {
        padding: var(--space-3) var(--space-4);
        border-top: 1px solid var(--color-border-secondary);
      }
      .edit-area textarea {
        width: 100%;
        min-height: 80px;
        background: var(--color-bg-primary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        color: var(--color-text-primary);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        padding: var(--space-3);
        resize: vertical;
        box-sizing: border-box;
      }
      .edit-area textarea:focus {
        outline: none;
        border-color: var(--color-primary);
      }
      .edit-submit-row {
        display: flex;
        justify-content: flex-end;
        gap: var(--space-2);
        margin-top: var(--space-2);
      }

      .user-notes {
        padding: var(--space-2) var(--space-4) var(--space-3);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-secondary);
        font-style: italic;
      }

      /* ─── Execution Output ─── */
      .execution-panel {
        border-top: 1px solid var(--color-border-secondary);
        background: #0d1117;
        padding: var(--space-3) var(--space-4);
        max-height: 300px;
        overflow-y: auto;
      }
      .execution-panel::-webkit-scrollbar { width: 6px; }
      .execution-panel::-webkit-scrollbar-track { background: #161b22; }
      .execution-panel::-webkit-scrollbar-thumb { background: #30363d; border-radius: 3px; }
      .execution-header {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        margin-bottom: var(--space-2);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        font-weight: var(--font-semibold);
        color: #58a6ff;
      }
      .exec-spinner {
        width: 12px; height: 12px;
        border: 2px solid rgba(88,166,255,0.2);
        border-top-color: #58a6ff;
        border-radius: 50%;
        animation: exec-spin 0.8s linear infinite;
      }
      @keyframes exec-spin { to { transform: rotate(360deg); } }
      .execution-line {
        font-family: var(--font-mono);
        font-size: 0.6875rem;
        line-height: 1.6;
        color: #8b949e;
        white-space: pre-wrap;
        word-break: break-all;
      }
      .execution-line.thinking { color: #7ee787; font-style: italic; }
      .execution-line.tool { color: #d2a8ff; }
      .execution-line.result { color: #8b949e; }
      .execution-line.error { color: #f85149; }
      .execution-line.complete { color: #58a6ff; font-weight: 600; }
      .execution-line.output { color: #c9d1d9; }
      .exec-done {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        margin-top: var(--space-2);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: #3fb950;
        font-weight: var(--font-semibold);
      }
      .exec-toggle {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2) var(--space-4);
        border-top: 1px solid var(--color-border-secondary);
        background: #0d1117;
        cursor: pointer;
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: #58a6ff;
        user-select: none;
      }
      .exec-toggle:hover { background: #161b22; }
      .exec-toggle .exec-status {
        margin-left: auto;
        font-family: var(--font-mono);
        font-size: 0.625rem;
      }
      .exec-toggle .exec-status.running { color: #d29922; }
      .exec-toggle .exec-status.done { color: #3fb950; }

      .empty-state {
        text-align: center;
        padding: var(--space-8);
        color: var(--color-text-tertiary);
      }
      .empty-state i {
        font-size: 2rem;
        opacity: 0.3;
        margin-bottom: var(--space-3);
        display: block;
      }
    `
  ];

  constructor() {
    super();
    this._proposals = [];
    this._loading = true;
    this._page = 1;
    this._totalPages = 1;
    this._expandedId = null;
    this._editingId = null;
    this._editText = '';
    this._actionInProgress = null;
    this._filter = '';
    this._executingId = null;
    this._executionOutputs = {};  // { proposalId: { lines: [], done: bool } }
    this._expandedOutputId = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this._loadProposals();
    this._unsubProposal = liveCallsService.onMonitorProposal(() => {
      this._loadProposals();
    });
    // Listen for monitor log lines while executing
    this._unsubLog = liveCallsService.onMonitorLog((line) => {
      if (!this._executingId) return;
      this._routeExecutionLine(line);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._unsubProposal) this._unsubProposal();
    if (this._unsubLog) this._unsubLog();
  }

  async _loadProposals() {
    this._loading = true;
    try {
      const data = await settingsService.loadMonitorProposals(this._page, 20, this._filter);
      if (data.success) {
        this._proposals = data.proposals || [];
        this._totalPages = data.pages || 1;
      }
    } catch (err) {
      console.error('[SelfHealingProposals] Load failed:', err);
    } finally {
      this._loading = false;
    }
  }

  _setFilter(status) {
    this._filter = this._filter === status ? '' : status;
    this._page = 1;
    this._loadProposals();
  }

  _toggleExpand(id) {
    this._expandedId = this._expandedId === id ? null : id;
  }

  async _executeProposal(id) {
    if (!confirm('Execute this proposal? Claude will attempt to implement the plan.')) return;
    this._actionInProgress = id;
    this._executingId = id;
    this._expandedOutputId = id;
    this._executionOutputs = {
      ...this._executionOutputs,
      [id]: { lines: [{ cls: 'output', text: 'Sending to Claude...' }], done: false }
    };
    try {
      await settingsService.updateProposal(id, 'approve');
      this._loadProposals();
    } catch (err) {
      this._executingId = null;
      this.dispatchEvent(new CustomEvent('show-toast', {
        bubbles: true, composed: true,
        detail: { message: 'Failed to approve proposal', type: 'error' }
      }));
    } finally {
      this._actionInProgress = null;
    }
  }

  _routeExecutionLine(line) {
    if (!line || !this._executingId) return;
    const id = this._executingId;
    // Skip separator lines
    if (line.startsWith('\u2550') || line.startsWith('\u2500')) return;

    let cls = 'output';
    let text = line;

    if (line.startsWith('[THINKING]')) {
      cls = 'thinking';
      text = line.replace('[THINKING] ', '');
    } else if (line.startsWith('[TOOL]')) {
      cls = 'tool';
      text = this._humanizeTool(line);
    } else if (line.startsWith('[TOOL RESULT]')) {
      cls = 'result';
      text = 'Done';
    } else if (line.startsWith('[TOOL ERROR]')) {
      cls = 'error';
      text = line.replace('[TOOL ERROR] ', '');
    } else if (line.startsWith('[COMPLETE]')) {
      cls = 'complete';
      text = line.replace('[COMPLETE] ', '');
      // Mark done, update status to executed
      const output = this._executionOutputs[id];
      if (output) output.done = true;
      this._executingId = null;
      this._executionOutputs = { ...this._executionOutputs };
      // Update proposal status to 'executed' in the backend
      this._markExecuted(id);
      return;
    } else if (line.startsWith('[MONITOR]')) {
      cls = 'output';
      text = line.replace('[MONITOR] ', '');
    } else if (line.match(/^\[[\d-]+ [\d:]+ \w+\]/)) {
      text = line.replace(/^\[.*?\]\s*/, '');
      if (!text || text.startsWith('[INFO]')) return;
    }

    const output = this._executionOutputs[id] || { lines: [], done: false };
    output.lines = [...output.lines, { cls, text }];
    this._executionOutputs = { ...this._executionOutputs, [id]: output };
    // Auto-scroll execution panel
    this.updateComplete.then(() => {
      const panel = this.renderRoot?.querySelector(`.execution-panel[data-id="${id}"]`);
      if (panel) panel.scrollTop = panel.scrollHeight;
    });
  }

  async _markExecuted(id) {
    // Collect the last few meaningful output lines as execution result
    const output = this._executionOutputs[id];
    let resultText = '';
    if (output?.lines) {
      resultText = output.lines
        .filter(l => l.cls === 'output' || l.cls === 'complete')
        .slice(-10)
        .map(l => l.text)
        .join('\n');
    }
    try {
      await settingsService.updateProposal(id, 'execute', { result: resultText });
    } catch (e) {
      console.warn('[Proposals] Failed to mark executed:', e);
    }
    this._loadProposals();
  }

  _toggleOutput(id) {
    this._expandedOutputId = this._expandedOutputId === id ? null : id;
  }

  _renderExecutionOutput(p) {
    const output = this._executionOutputs[p.id];
    const isExecuting = this._executingId === p.id;
    const hasOutput = output && output.lines.length > 0;
    const isExpanded = this._expandedOutputId === p.id;

    // Nothing to show
    if (!hasOutput && !p.execution_result) return '';

    // Accordion toggle bar
    const toggleLabel = isExecuting ? 'Execution Output' : 'Execution Output';
    const statusLabel = isExecuting
      ? html`<span class="exec-status running"><span class="exec-spinner" style="display:inline-block;width:10px;height:10px;vertical-align:middle;margin-right:4px"></span> Running...</span>`
      : output?.done
        ? html`<span class="exec-status done">Complete</span>`
        : p.status === 'executed'
          ? html`<span class="exec-status done">Complete</span>`
          : '';

    return html`
      <div class="exec-toggle" @click=${() => this._toggleOutput(p.id)}>
        <i class="fa-sharp fa-light fa-${isExpanded ? 'chevron-down' : 'chevron-right'}"></i>
        ${toggleLabel}
        ${statusLabel}
      </div>
      ${isExpanded ? html`
        <div class="execution-panel" data-id="${p.id}">
          ${hasOutput ? output.lines.map(l => html`
            <div class="execution-line ${l.cls}">${l.text}</div>
          `) : ''}
          ${!hasOutput && p.execution_result ? html`
            <div class="execution-line output">${p.execution_result}</div>
          ` : ''}
          ${output?.done ? html`
            <div class="exec-done">
              <i class="fa-sharp fa-light fa-check-circle"></i>
              Execution complete
            </div>
          ` : ''}
        </div>
      ` : ''}
    `;
  }

  _humanizeTool(line) {
    if (line.includes('pipeline-config')) return 'Updating pipeline settings...';
    if (line.includes('cache/invalidate')) return 'Refreshing config cache...';
    if (line.includes('curl')) return 'Calling API...';
    if (line.includes('Reading:')) {
      const fname = line.replace('[TOOL] Reading: ', '').split('/').pop();
      return `Reading ${fname}...`;
    }
    return 'Running tool...';
  }

  async _denyProposal(id) {
    const reason = prompt('Reason for denial (optional):');
    if (reason === null) return; // cancelled
    this._actionInProgress = id;
    try {
      await settingsService.updateProposal(id, 'deny', { reason });
      this.dispatchEvent(new CustomEvent('show-toast', {
        bubbles: true, composed: true,
        detail: { message: 'Proposal denied', type: 'info' }
      }));
      this._loadProposals();
    } catch (err) {
      this.dispatchEvent(new CustomEvent('show-toast', {
        bubbles: true, composed: true,
        detail: { message: 'Failed to deny proposal', type: 'error' }
      }));
    } finally {
      this._actionInProgress = null;
    }
  }

  _startEdit(id) {
    this._editingId = id;
    this._editText = '';
  }

  _cancelEdit() {
    this._editingId = null;
    this._editText = '';
  }

  async _submitEdit(id) {
    if (!this._editText.trim()) return;
    this._actionInProgress = id;
    try {
      await settingsService.updateProposal(id, 'edit', { instructions: this._editText });
      this._editingId = null;
      this._editText = '';
      this.dispatchEvent(new CustomEvent('show-toast', {
        bubbles: true, composed: true,
        detail: { message: 'Edit instructions sent to Claude', type: 'success' }
      }));
    } catch (err) {
      this.dispatchEvent(new CustomEvent('show-toast', {
        bubbles: true, composed: true,
        detail: { message: 'Failed to send edit', type: 'error' }
      }));
    } finally {
      this._actionInProgress = null;
    }
  }

  _formatDate(isoStr) {
    if (!isoStr) return '';
    try {
      const d = new Date(isoStr + 'Z');
      return d.toLocaleString('en-US', {
        month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
        timeZone: tz.tz
      });
    } catch { return isoStr; }
  }

  render() {
    if (this._loading && this._proposals.length === 0) {
      return html`<div class="empty-state"><p>Loading proposals...</p></div>`;
    }

    return html`
      <div class="filter-row">
        <button class="filter-btn ${this._filter === '' ? 'active' : ''}" @click=${() => this._setFilter('')}>All</button>
        <button class="filter-btn ${this._filter === 'pending' ? 'active' : ''}" @click=${() => this._setFilter('pending')}>Pending</button>
        <button class="filter-btn ${this._filter === 'approved' ? 'active' : ''}" @click=${() => this._setFilter('approved')}>Approved</button>
        <button class="filter-btn ${this._filter === 'executed' ? 'active' : ''}" @click=${() => this._setFilter('executed')}>Executed</button>
        <button class="filter-btn ${this._filter === 'denied' ? 'active' : ''}" @click=${() => this._setFilter('denied')}>Denied</button>
      </div>

      ${this._proposals.length === 0 ? html`
        <div class="empty-state">
          <i class="fa-sharp fa-light fa-lightbulb"></i>
          <p>No proposals yet. When the monitor detects significant issues that need human approval, they'll appear here.</p>
        </div>
      ` : html`
        <div class="proposals-list">
          ${this._proposals.map(p => this._renderProposal(p))}
        </div>
      `}
    `;
  }

  _renderProposal(p) {
    const isExpanded = this._expandedId === p.id;
    const isEditing = this._editingId === p.id;
    const inProgress = this._actionInProgress === p.id;
    const isPending = p.status === 'pending';

    return html`
      <div class="proposal-card">
        <div class="proposal-header">
          <div class="proposal-status-row">
            <span class="status-badge ${p.status}">${p.status}</span>
            ${p.severity ? html`<span class="severity-tag ${p.severity}">${p.severity}</span>` : ''}
            ${p.category ? html`<span class="category-tag">${p.category}</span>` : ''}
          </div>
          <h4 class="proposal-title">${p.title}</h4>
          <span class="proposal-date">${this._formatDate(p.created_at)}</span>
        </div>

        <div class="proposal-plan-toggle" @click=${() => this._toggleExpand(p.id)}>
          <i class="fa-sharp fa-light fa-${isExpanded ? 'chevron-down' : 'chevron-right'}"></i>
          ${isExpanded ? 'Hide Plan' : 'View Full Plan'}
        </div>

        ${isExpanded ? html`
          <div class="proposal-plan">${p.plan}</div>
        ` : ''}

        ${p.user_notes ? html`
          <div class="user-notes">Note: ${p.user_notes}</div>
        ` : ''}

        ${isPending || p.status === 'approved' ? html`
          <div class="proposal-actions">
            <button class="action-btn btn-execute" @click=${() => this._executeProposal(p.id)} ?disabled=${inProgress || this._executingId === p.id}>
              <i class="fa-sharp fa-light fa-check"></i> ${p.status === 'approved' ? 'Re-Execute' : 'Execute'}
            </button>
            ${isPending ? html`
              <button class="action-btn btn-deny" @click=${() => this._denyProposal(p.id)} ?disabled=${inProgress}>
                <i class="fa-sharp fa-light fa-xmark"></i> Deny
              </button>
              <button class="action-btn btn-edit" @click=${() => this._startEdit(p.id)} ?disabled=${inProgress}>
                <i class="fa-sharp fa-light fa-pen"></i> Edit
              </button>
            ` : ''}
          </div>
        ` : ''}

        ${isEditing ? html`
          <div class="edit-area">
            <textarea
              placeholder="Provide instructions for Claude to revise this plan..."
              .value=${this._editText}
              @input=${e => { this._editText = e.target.value; }}
            ></textarea>
            <div class="edit-submit-row">
              <button class="action-btn" @click=${() => this._cancelEdit()} style="color:var(--color-text-secondary)">Cancel</button>
              <button class="action-btn btn-execute" @click=${() => this._submitEdit(p.id)} ?disabled=${inProgress || !this._editText.trim()}>
                Send to Claude
              </button>
            </div>
          </div>
        ` : ''}

        ${this._renderExecutionOutput(p)}
      </div>
    `;
  }
}

customElements.define('self-healing-proposals', SelfHealingProposals);
